// Commercial Acoustic Calculator — engine core (constants, context, absorption math).
// Attached to window.AcousticCalculatorEngine for browser use;
// evaluated under a window shim by tests/helpers/load-engine.js for Node use.
// Extracted from acoustic-calculator-commercial.js (Phase 1 refactor, no behavior change).

(function () {
  "use strict";

  const BANDS = ["250 Hz", "500 Hz", "1000 Hz", "2000 Hz", "4000 Hz"];
  const RT60_CONSTANT = 0.049;
  // 4-21-26 sheet row 77 "room volume effects": per-ft³ air-absorption
  // coefficients in sabins. 250 Hz and 500 Hz are zero per the sheet.
  // (Prior code stored {1.2, 3, 7} keyed per 1000 ft³ — same values.)
  const AIR_ABSORPTION_COEFFS = {
    "250 Hz": 0,
    "500 Hz": 0,
    "1000 Hz": 0.0012,
    "2000 Hz": 0.003,
    "4000 Hz": 0.007,
  };

  function toNum(x) {
    const n = parseFloat(x);
    return Number.isFinite(n) ? n : 0;
  }

  function round2(n) {
    return Math.round(n * 100) / 100;
  }

  function round4(n) {
    return Math.round(n * 10000) / 10000;
  }

  // Per-band air absorption (sabins) for a given room volume, sheet row 77.
  function computeAirAbsorption(volume) {
    const v = toNum(volume);
    const out = {};
    for (const band of BANDS) {
      out[band] = AIR_ABSORPTION_COEFFS[band] * v;
    }
    return out;
  }

  // Derive the geometry + catalog lookups that every engine method reads.
  // Consolidates the Vue component's computed properties and material finders
  // so downstream functions stay pure.
  function buildContext(inputs, catalog) {
    const length = toNum(inputs.room_length);
    const width = toNum(inputs.room_width);
    const height = toNum(inputs.room_height);

    const entrywayArea = Math.round(toNum(inputs.open_entryways) * 18.67);
    const totalWallArea = 2 * (length * height + width * height);
    const wallArea = Math.max(0, totalWallArea - entrywayArea);
    const ceilingArea = length * width;

    // Per-wall geometry: walls 1&2 span the room length, walls 3&4 span
    // the room width — matching the sheet's row 67-70 formulas (B8×B10
    // for rows 67-68, B9×B10 for rows 69-70).
    const rawWallAreas = [
      length * height,
      length * height,
      width * height,
      width * height,
    ];
    const entryPerWall = entrywayArea * 0.25;
    const wallSlots = rawWallAreas.map((a, i) => {
      const entry = (inputs.walls && inputs.walls[i]) || {};
      const materialId = entry.material_id || "";
      const material = materialId
        ? (catalog.wallMaterials || []).find((m) => m.value === materialId)
        : null;
      return {
        index: i,
        rawArea: a,
        effectiveArea: Math.max(0, a - entryPerWall),
        materialId,
        material,
      };
    });
    const floorArea = ceilingArea;
    const floorCoveringsArea = Math.round(
      floorArea * (toNum(inputs.floor_covering_pct) / 100),
    );

    const furnitureOpt = (catalog.furnitureOptions || []).find(
      (f) => f.value === inputs.furniture,
    );
    const furnitureMultiplier = furnitureOpt
      ? (furnitureOpt.floorCoveragePercent || 0) / 100
      : 0;
    const furnitureArea = floorArea * furnitureMultiplier;

    const roomVolume = length * width * height;
    const availableWallSqFt =
      totalWallArea * (toNum(inputs.available_wall_space) / 100);
    const availableCeilingSqFt =
      ceilingArea * (toNum(inputs.available_ceiling_space) / 100);

    const ceilingMaterial = (catalog.ceilingMaterials || []).find(
      (m) => m.value === inputs.ceiling_material,
    );
    const floorMaterial = (catalog.floorMaterials || []).find(
      (m) => m.value === inputs.floor_material,
    );
    const wallProduct = (catalog.feltRightProducts?.wall || []).find(
      (p) => p.id === inputs.wall_product,
    );
    const ceilingProduct = (catalog.feltRightProducts?.ceiling || []).find(
      (p) => p.id === inputs.ceiling_product,
    );

    return {
      inputs,
      catalog,
      wallArea,
      wallSlots,
      entrywayArea,
      ceilingArea,
      floorArea,
      floorCoveringsArea,
      furnitureArea,
      roomVolume,
      availableWallSqFt,
      availableCeilingSqFt,
      ceilingMaterial,
      floorMaterial,
      wallProduct,
      ceilingProduct,
    };
  }

  // Sabine RT60. Original guard: 0 absorption returns 999 (very reverberant).
  function calculateRT60(volume, totalAbsorption) {
    if (totalAbsorption === 0) return 999;
    return round4((RT60_CONSTANT * volume) / totalAbsorption);
  }

  function calculateRequiredAbsorption(volume, targetRT60) {
    if (targetRT60 === 0) return 0;
    return round4((RT60_CONSTANT * volume) / targetRT60);
  }

  // Total existing absorption (pre-treatment) for one frequency band.
  // Each element is rounded to 2 decimals before summing to match the
  // sheet's per-cell ROUND(coeff*area, 2) behavior.
  function calculateExistingAbsorption(frequency, ctx) {
    const {
      wallSlots,
      ceilingMaterial,
      floorMaterial,
      ceilingArea,
      floorArea,
      floorCoveringsArea,
      furnitureArea,
      entrywayArea,
      roomVolume,
      inputs,
      catalog,
    } = ctx;

    let total = 0;

    for (const slot of wallSlots || []) {
      if (slot.material) {
        total += round2(
          slot.effectiveArea * slot.material.absorption[frequency],
        );
      }
    }

    if (toNum(inputs.open_entryways) > 0 && catalog.openEntrywayAbsorption) {
      total += round2(entrywayArea * catalog.openEntrywayAbsorption[frequency]);
    }

    if (floorMaterial) {
      total += round2(floorArea * floorMaterial.absorption[frequency]);
    }

    if (toNum(inputs.floor_covering_pct) > 0) {
      const coveringId =
        inputs.floor_covering_id && inputs.floor_covering_id !== "none"
          ? inputs.floor_covering_id
          : "light_carpet";
      const covering = (catalog.floorCoverings || []).find(
        (c) => c.value === coveringId,
      );
      if (covering && covering.absorption[frequency] != null) {
        total += round2(floorCoveringsArea * covering.absorption[frequency]);
      }
    }

    if (ceilingMaterial) {
      total += round2(ceilingArea * ceilingMaterial.absorption[frequency]);
    }

    if (inputs.furniture !== "none" && catalog.furnitureAbsorption) {
      total += round2(furnitureArea * catalog.furnitureAbsorption[frequency]);
    }

    if (
      inputs.room_dividers_enabled &&
      toNum(inputs.room_divider_quantity) > 0
    ) {
      const divider = catalog.roomDivider;
      if (
        divider &&
        divider.absorption &&
        divider.absorption[frequency] != null
      ) {
        const sheetArea = divider.sheetArea || divider.footprint || 1;
        const dividerScale =
          divider.footprint > 0 ? sheetArea / divider.footprint : 1;
        total += round2(
          toNum(inputs.room_divider_quantity) *
            divider.absorption[frequency] *
            dividerScale,
        );
      }
    }

    total += (AIR_ABSORPTION_COEFFS[frequency] || 0) * roomVolume;
    return round4(total);
  }

  // Sheet row 77: existing absorption WITHOUT room dividers.
  // Used by the ACT benchmark equivalent sizing (row 128).
  function calculateExistingAbsorptionNoDividers(frequency, ctx) {
    const saved = ctx.inputs.room_dividers_enabled;
    ctx.inputs.room_dividers_enabled = false;
    const result = calculateExistingAbsorption(frequency, ctx);
    ctx.inputs.room_dividers_enabled = saved;
    return result;
  }

  // RT60 after adding wall + ceiling product quantities on top of existing absorption.
  function calculateRT60WithProducts(
    frequency,
    wallQuantity,
    ceilingQuantity,
    ctx,
  ) {
    let absorption = calculateExistingAbsorption(frequency, ctx);
    if (wallQuantity > 0 && ctx.wallProduct) {
      absorption += wallQuantity * ctx.wallProduct.absorption[frequency];
    }
    if (ceilingQuantity > 0 && ctx.ceilingProduct) {
      absorption += ceilingQuantity * ctx.ceilingProduct.absorption[frequency];
    }
    return calculateRT60(ctx.roomVolume, absorption);
  }

  // Dynamic reverb-bar scale spanning both recommended and existing RT60.
  // Returns { min, max, ticks } with even spacing that covers both values.
  function computeBarScale(recommendedRT60, existingRT60) {
    var a = Math.round((Number(recommendedRT60) || 0) * 100) / 100;
    var b = Math.round((Number(existingRT60) || 0) * 100) / 100;
    var lo = Math.min(a, b);
    var hi = Math.max(a, b);
    if (hi - lo < 0.05) hi = lo + 0.1;

    var PAD = 1;
    var scaleMin = Math.max(0, lo - PAD);
    var scaleMax = hi + PAD;
    if (scaleMax - scaleMin < 2) scaleMax = scaleMin + 2;

    var range = scaleMax - scaleMin;
    var TICK_COUNT = 5;
    var rawStep = range / TICK_COUNT;
    var steps = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5];
    var step =
      steps.find(function (s) {
        return s >= rawStep;
      }) || rawStep;
    var tickMin = Math.floor(scaleMin / step) * step;
    var tickMax = Math.ceil(scaleMax / step) * step;
    tickMin = Math.round(tickMin * 100) / 100;
    tickMax = Math.round(tickMax * 100) / 100;
    if (tickMin === tickMax) tickMax = tickMin + step;
    var ticks = [];
    var count = Math.round((tickMax - tickMin) / step);
    for (var i = 0; i <= count; i++) {
      ticks.push(Math.round((tickMin + i * step) * 100) / 100);
    }

    var sliderMin = Math.round(Math.min(a, b) * 100) / 100;
    var sliderMax = Math.round(Math.max(a, b) * 100) / 100;

    return {
      min: tickMin,
      max: tickMax,
      ticks: ticks,
      sliderMin: sliderMin,
      sliderMax: sliderMax,
    };
  }

  var BAR_SCALE_MAX = 2.4;
  var BAR_SCALE_TICKS = [0, 0.4, 0.8, 1.2, 1.6, 2.0, 2.4];
  var BAR_SEGMENT_COUNT = 60;
  var BAR_SEGMENT_START_COLOR = "#242B64";
  var BAR_SEGMENT_END_COLOR = "#818181";

  function hexToRgb(hex) {
    var value = String(hex || "").replace("#", "");
    return {
      r: parseInt(value.slice(0, 2), 16),
      g: parseInt(value.slice(2, 4), 16),
      b: parseInt(value.slice(4, 6), 16),
    };
  }

  function componentToHex(component) {
    return Math.round(component).toString(16).padStart(2, "0");
  }

  function interpolateHexColor(startHex, endHex, pct) {
    var start = hexToRgb(startHex);
    var end = hexToRgb(endHex);
    var t = Math.max(0, Math.min(1, Number(pct) || 0));
    return (
      "#" +
      componentToHex(start.r + (end.r - start.r) * t) +
      componentToHex(start.g + (end.g - start.g) * t) +
      componentToHex(start.b + (end.b - start.b) * t)
    );
  }

  function computeReverbScaleSegments(count) {
    var total = Math.max(2, Number(count) || BAR_SEGMENT_COUNT);
    var segments = [];
    for (var i = 0; i < total; i++) {
      var pct = total > 1 ? (i / (total - 1)) * 100 : 0;
      segments.push({
        index: i,
        pct: pct,
        color: interpolateHexColor(
          BAR_SEGMENT_START_COLOR,
          BAR_SEGMENT_END_COLOR,
          pct / 100,
        ),
      });
    }
    return segments;
  }

  function rtToPct(rt, scaleMin, scaleMax) {
    var lo = scaleMin || 0;
    var hi = scaleMax || BAR_SCALE_MAX;
    var n = Number(rt);
    if (!Number.isFinite(n) || hi <= lo) return 0;
    if (n <= lo) return 0;
    if (n >= hi) return 100;
    return ((n - lo) / (hi - lo)) * 100;
  }

  function computeReverbBarPositions({ existing, starter, recommended }) {
    return {
      existingPct: rtToPct(existing),
      starterPct: rtToPct(starter),
      recommendedPct: rtToPct(recommended),
    };
  }

  var MERGE_THRESHOLD = 3;
  var KEY_ORDER = { recommended: 0, starter: 1, existing: 2 };

  var MERGED_LABEL_MAP = {
    "recommended+existing": "Recommended /<br>Existing Level",
    "recommended+starter": "Recommended /<br>Starter Qty",
    "starter+existing": "Starter /<br>Existing Level",
    "recommended+starter+existing": "Recommended / Starter /<br>Existing Level",
  };

  function mergeReverbMarkers(positions, labels, values) {
    values = values || {};
    var raw = [
      { key: "recommended", pct: positions.recommendedPct },
      { key: "starter", pct: positions.starterPct },
      { key: "existing", pct: positions.existingPct },
    ];
    raw.sort(function (a, b) {
      return a.pct - b.pct;
    });
    var groups = [[raw[0]]];
    for (var i = 1; i < raw.length; i++) {
      var last = groups[groups.length - 1];
      if (raw[i].pct - last[last.length - 1].pct < MERGE_THRESHOLD)
        last.push(raw[i]);
      else groups.push([raw[i]]);
    }
    return groups.map(function (group) {
      var keys = group.map(function (m) {
        return m.key;
      });
      keys.sort(function (a, b) {
        return KEY_ORDER[a] - KEY_ORDER[b];
      });
      var pctSum = 0;
      group.forEach(function (m) {
        pctSum += m.pct;
      });
      var lookupKey = keys.join("+");
      return {
        keys: keys,
        pct: pctSum / group.length,
        label: MERGED_LABEL_MAP[lookupKey] || labels[keys[0]],
        value: values[keys[0]] || "",
      };
    });
  }

  // 1D label placement for the reverb scroller marker labels.
  // All labels render on ONE shared row below the bar; when markers
  // cluster, labels fan out horizontally (leftmost stays centered under
  // its marker, the rest push right) and clamp inside the container.
  // Matches the Figma Examples frame (node 967:3056) generalized to N
  // markers. Returns [{ key, pct, labelCenterPx, clusterIndex }] sorted
  // left-to-right. clusterIndex is the 0-based position within the
  // marker's collision cluster (labels still at minimum separation after
  // solving); singletons get 0.
  var LABEL_TIE_ORDER = ["existing", "starter", "target", "recommended"];

  function solveMarkerLabelRow(markers, widths, containerPx, gapPx) {
    var gap = gapPx == null ? 8 : gapPx;
    var sorted = (markers || []).slice().sort(function (a, b) {
      if (a.pct !== b.pct) return a.pct - b.pct;
      return LABEL_TIE_ORDER.indexOf(a.key) - LABEL_TIE_ORDER.indexOf(b.key);
    });
    var n = sorted.length;
    if (n === 0) return [];

    var w = sorted.map(function (m) {
      return (widths && widths[m.key]) || 0;
    });
    var x = sorted.map(function (m) {
      return (m.pct / 100) * containerPx;
    });

    // Forward pass: push overlapping labels right.
    var i;
    for (i = 1; i < n; i++) {
      var minX = x[i - 1] + (w[i - 1] + w[i]) / 2 + gap;
      if (x[i] < minX) x[i] = minX;
    }
    // Clamp right edge, then backward pass pushes back left.
    var maxRight = containerPx - w[n - 1] / 2;
    if (x[n - 1] > maxRight) x[n - 1] = maxRight;
    for (i = n - 2; i >= 0; i--) {
      var maxX = x[i + 1] - (w[i] + w[i + 1]) / 2 - gap;
      if (x[i] > maxX) x[i] = maxX;
    }
    // Clamp left edge, forward-push once more (may compress gaps at the
    // right edge if total width exceeds the container -- acceptable).
    if (x[0] < w[0] / 2) x[0] = w[0] / 2;
    for (i = 1; i < n; i++) {
      var minX2 = x[i - 1] + (w[i - 1] + w[i]) / 2 + gap;
      if (x[i] < minX2) x[i] = minX2;
    }

    // Cluster detection: chained labels still at (or within 1px of)
    // minimum separation belong to one cluster.
    var result = [];
    var clusterIndex = 0;
    for (i = 0; i < n; i++) {
      if (i > 0) {
        var minSep = (w[i - 1] + w[i]) / 2 + gap;
        clusterIndex = x[i] - x[i - 1] <= minSep + 1 ? clusterIndex + 1 : 0;
      }
      result.push({
        key: sorted[i].key,
        pct: sorted[i].pct,
        labelCenterPx: x[i],
        clusterIndex: clusterIndex,
      });
    }
    return result;
  }

  const ns = (window.AcousticCalculatorEngine =
    window.AcousticCalculatorEngine || {});
  ns.BANDS = BANDS;
  ns.RT60_CONSTANT = RT60_CONSTANT;
  ns.AIR_ABSORPTION_COEFFS = AIR_ABSORPTION_COEFFS;
  ns.toNum = toNum;
  ns.round4 = round4;
  ns.buildContext = buildContext;
  ns.computeAirAbsorption = computeAirAbsorption;
  ns.calculateRT60 = calculateRT60;
  ns.calculateRequiredAbsorption = calculateRequiredAbsorption;
  ns.calculateExistingAbsorption = calculateExistingAbsorption;
  ns.calculateExistingAbsorptionNoDividers =
    calculateExistingAbsorptionNoDividers;
  ns.calculateRT60WithProducts = calculateRT60WithProducts;
  ns.BAR_SCALE_MAX = BAR_SCALE_MAX;
  ns.BAR_SCALE_TICKS = BAR_SCALE_TICKS;
  ns.BAR_SEGMENT_COUNT = BAR_SEGMENT_COUNT;
  ns.BAR_SEGMENT_START_COLOR = BAR_SEGMENT_START_COLOR;
  ns.BAR_SEGMENT_END_COLOR = BAR_SEGMENT_END_COLOR;
  ns.computeReverbScaleSegments = computeReverbScaleSegments;
  ns.computeBarScale = computeBarScale;
  ns.rtToPct = rtToPct;
  ns.computeReverbBarPositions = computeReverbBarPositions;
  ns.mergeReverbMarkers = mergeReverbMarkers;
  ns.solveMarkerLabelRow = solveMarkerLabelRow;
})();
